import type { Def, FlagValue } from '@mss_creator/shared';
import { TWEAK_DEFS } from '@mss_creator/shared';
import { useMemo, useSyncExternalStore } from 'react';

/**
 * localStorage engine for the settings page's tweaks (`kind: 'tweak'` registry entries).
 * The only module allowed to touch localStorage for tweak keys — reads go
 * through `getTweakValue`/`useTweakValue`/`useTweakByKey`, writes through
 * `setTweakValue`. Flags (`kind: 'flag'`) never touch this module — they're
 * backend-persisted, see `lib/api.ts`'s `featureFlagsApi`.
 */

function storageKey(def: Def): string {
  return `tweak.${def.key}`;
}

function readRaw(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // localStorage may be unavailable (private mode, SSR) — degrade silently.
  }
}

function removeRaw(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // ignore
  }
}

function parseValue(def: Def, raw: string | null): FlagValue {
  if (raw === null) return def.default;
  switch (def.type) {
    case 'boolean':
      return raw === 'true';
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : def.default;
    }
    case 'enum':
      return def.options.some((option) => option.value === raw) ? raw : def.default;
    case 'string':
      return raw;
  }
}

function tweakDefByKey(key: string): Def | undefined {
  return TWEAK_DEFS.find((def) => def.key === key);
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (): void => listener();
  globalThis.addEventListener?.('storage', onStorage);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', onStorage);
  };
}

/** Current parsed value for a def (its default when unset/invalid). */
export function getTweakValue(def: Def): FlagValue {
  return parseValue(def, readRaw(storageKey(def)));
}

/** Persist a new value for a tweak and notify subscribers. */
export function setTweakValue(def: Def, value: FlagValue): void {
  writeRaw(storageKey(def), String(value));
  emit();
}

/** Subscribe to a tweak's value reactively. Used by the settings page's own rows. */
export function useTweakValue(def: Def): FlagValue {
  return useSyncExternalStore(
    subscribe,
    () => getTweakValue(def),
    () => def.default,
  );
}

/** Subscribe to a tweak's value reactively by key. Used by app code outside the settings page (e.g. `effective-role.ts`'s `useEffectiveRole`). */
export function useTweakByKey(key: string): FlagValue | undefined {
  const def = tweakDefByKey(key);
  return useSyncExternalStore(
    subscribe,
    () => (def ? getTweakValue(def) : undefined),
    () => def?.default,
  );
}

/** Remove one tweak's stored value, so it reads as its default again, and notify subscribers. */
export function resetTweak(def: Def): void {
  removeRaw(storageKey(def));
  emit();
}

/**
 * The six visual tweaks of the settings page's "Affichage" tab: every tweak
 * outside the `systeme` section, which holds the simulated role.
 */
export const DISPLAY_TWEAK_DEFS: readonly Def[] = TWEAK_DEFS.filter(
  (def) => def.section !== 'systeme',
);

/**
 * Put the visual tweaks back to their defaults and notify subscribers. Leaves
 * the role override alone: an active role simulation is not a display choice.
 */
export function resetDisplayTweaks(): void {
  for (const def of DISPLAY_TWEAK_DEFS) removeRaw(storageKey(def));
  emit();
}

function readAllTweaks(): string {
  return JSON.stringify(TWEAK_DEFS.map((def) => [def.key, String(getTweakValue(def))]));
}

function defaultTweaks(): string {
  return JSON.stringify(TWEAK_DEFS.map((def) => [def.key, String(def.default)]));
}

/**
 * Current value of every tweak, by key, as strings. Subscribes to the whole
 * store: used by the settings page, whose miniatures depend on more than one
 * tweak (the activation colours follow the activation names).
 */
export function useTweakValues(): Readonly<Record<string, string>> {
  const snapshot = useSyncExternalStore(subscribe, readAllTweaks, defaultTweaks);
  return useMemo(() => {
    const entries = JSON.parse(snapshot) as [string, string][];
    return Object.fromEntries(entries);
  }, [snapshot]);
}
